import clsx from 'clsx';
import { ReactNode } from 'react';
import { EllipsisHorizontalIcon } from '@heroicons/react/24/outline';

import Chevron from '~/shared/assets/icons/chevron.svg';
import ChevronDouble from '~/shared/assets/icons/chevron-double-left.svg';
import { DATA_LIMIT } from '~/fleet-manager/components/FleetManagerPage/AllVolumesPage/AllVolumesTable/AllVolumesTable';

import Typography from '../Typography';

import { usePagination, DOTS } from './usePagination';

interface TablePaginationProps {
  currentPage: number;
  goToPage: (pageNumber: number) => void;
  totalPages: number;
  siblingCount?: number;
  boundaryCount?: number;
  dataCount: number;
  perPage?: number;
  itemsName: string;
}

export default function TablePagination({
  totalPages,
  currentPage,
  goToPage,
  siblingCount = 1,
  boundaryCount = 1,
  dataCount,
  perPage = DATA_LIMIT,
  itemsName,
}: TablePaginationProps) {
  const canGoBack = currentPage > 0;
  const canGoForward = currentPage < totalPages - 1;

  const paginationRange = usePagination({
    currentPage,
    totalPages,
    siblingCount,
    boundaryCount,
  });

  const onNext = () => {
    if (canGoForward) {
      goToPage(currentPage + 1);
    }
  };

  const onPrevious = () => {
    if (canGoBack) {
      goToPage(currentPage - 1);
    }
  };

  const onGoToFirst = () => goToPage(0);
  const onGoToLast = () => goToPage(totalPages - 1);

  const navButtonBaseClasses =
    'flex items-center gap-[4px] border border-[#E4E6EB] text-sm rounded-lg text-[#313131] h-9 px-3 py-2 hover:bg-gray-100';
  const iconBaseClasses = 'size-4 fill-[#313131]';

  return (
    <div className="flex items-center justify-between gap-[4px] min-h-[32px]">
      <div className="flex items-center gap-[4px] justify-center min-h-[32px] text-sm">
        <button
          onClick={onGoToFirst}
          className={clsx(
            navButtonBaseClasses,
            !canGoBack ? 'opacity-50 pointer-events-none cursor-default' : 'cursor-pointer'
          )}
          disabled={!canGoBack}
          aria-label="Go to first page"
        >
          <ChevronDouble className={clsx(iconBaseClasses, 'rotate-180')} />
          <Typography variant="p3">First</Typography>
        </button>
        <button
          onClick={onPrevious}
          className={clsx(
            navButtonBaseClasses,
            !canGoBack ? 'opacity-50 pointer-events-none cursor-default' : 'cursor-pointer'
          )}
          disabled={!canGoBack}
          aria-label="Go to previous page"
        >
          <Chevron className={clsx(iconBaseClasses, 'rotate-90')} />
          <Typography variant="p3">Back</Typography>
        </button>

        {paginationRange.map((page, index) => {
          const key = typeof page === 'number' ? `page-${page}` : `dots-${index}`;
          if (page === DOTS) {
            return (
              <div key={key} className="flex items-center justify-center px-1 mx-0.5">
                <EllipsisHorizontalIcon className="w-5 h-5 text-gray-500" />
              </div>
            );
          }
          return (
            <PaginationItem
              key={key}
              onClick={() => goToPage(page as number)}
              active={(page as number) === currentPage}
            >
              {(page as number) + 1}
            </PaginationItem>
          );
        })}

        <button
          onClick={onNext}
          className={clsx(
            navButtonBaseClasses,
            !canGoForward ? 'opacity-50 pointer-events-none cursor-default' : 'cursor-pointer'
          )}
          disabled={!canGoForward}
          aria-label="Go to next page"
        >
          <Typography variant="p3">Next</Typography>
          <Chevron className={clsx(iconBaseClasses, '-rotate-90')} />
        </button>
        <button
          onClick={onGoToLast}
          className={clsx(
            navButtonBaseClasses,
            !canGoForward ? 'opacity-50 pointer-events-none cursor-default' : 'cursor-pointer'
          )}
          disabled={!canGoForward}
          aria-label="Go to last page"
        >
          <Typography variant="p3">Last</Typography>
          <ChevronDouble className={clsx(iconBaseClasses, '')} />
        </button>
      </div>

      <div>
        <p className="text-primary-900 text-[13px] leading-[20px]">
          {dataCount === 0
            ? `0 ${itemsName}`
            : `${currentPage * perPage + 1} - ${Math.min(currentPage * perPage + (perPage || 0), dataCount)} out of ${dataCount} ${itemsName}`}
        </p>
      </div>
    </div>
  );
}

interface PaginationItemProps {
  children: ReactNode;
  active?: boolean;
  onClick?: () => void;
  disabled?: boolean;
}

function PaginationItem({
  children,
  active = false,
  onClick,
  disabled = false,
}: PaginationItemProps) {
  const commonClasses =
    'text-[13px] leading-[20px] size-9 rounded-lg flex items-center justify-center text-center border border-[#E4E6EB]';

  if (disabled) {
    return <div className={clsx(commonClasses, 'text-[#949EAC] cursor-default')}>{children}</div>;
  }

  return (
    <button
      onClick={onClick}
      className={clsx(
        commonClasses,
        active
          ? 'text-white bg-primary-800 border-primary-800'
          : 'text-[#313131] hover:bg-gray-100',
        'cursor-pointer'
      )}
      disabled={active}
      aria-current={active ? 'page' : undefined}
      aria-label={active ? `Current page, Page ${children}` : `Go to page ${children}`}
    >
      {children}
    </button>
  );
}
